import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { FiArchive, FiEye, FiInbox, FiUser } from 'react-icons/fi';

import BasePage from '../../components/BasePage';
import DataTable, { Column, FilterField } from '../../components/DataTable';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/useToast';

import {
  StatusBadge,
  PriorityBadge,
  ActionButton,
  QuickButton,
} from './styles';

interface ICompany {
  id: string;
  nome_fantasia: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface IState {
  id: number;
  uf: string;
}

interface IComplaint {
  id: string;
  protocol: string;
  status: number;
  priority: number;
  company_id: string;
  company: {
    id: string;
    nome_fantasia: string;
  };
  city: {
    id: string;
    name: string;
  };
  is_archived: boolean;
  created_at: Date;
  updated_at: Date;
}

const STATUS_OPTIONS = [
  { value: '1', label: 'Recebido' },
  { value: '2', label: 'Em análise' },
  { value: '3', label: 'Finalizado' },
];

const PRIORITY_OPTIONS = [
  { value: '0', label: 'Não urgente' },
  { value: '1', label: 'Pouco urgente' },
  { value: '2', label: 'Normal' },
  { value: '3', label: 'Urgente' },
  { value: '4', label: 'Muito urgente' },
];

const STATUS_TONE: Record<number, 'success' | 'warning' | 'neutral' | 'info'> =
  {
    1: 'warning',
    2: 'info',
    3: 'success',
  };
const STATUS_LABEL: Record<number, string> = {
  1: 'Recebido',
  2: 'Em análise',
  3: 'Finalizado',
};

const PRIORITY_TONE: Record<number, 'neutral' | 'info' | 'warning' | 'danger'> =
  {
    0: 'neutral',
    1: 'neutral',
    2: 'info',
    3: 'warning',
    4: 'danger',
  };

type ViewMode = 'all' | 'mine' | 'archived';

const Complaints: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState<IComplaint[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<ViewMode>('all');

  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [unions, setUnions] = useState<ISyndicate[]>([]);
  const [states, setStates] = useState<IState[]>([]);

  useEffect(() => {
    document.title = 'Denúncias - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const extra: Record<string, unknown> = { iscovid: false };
        if (viewMode === 'mine') extra.assignedUsers = user.id;
        if (viewMode === 'archived') extra.is_archived = true;

        const response = await api.get<IComplaint[]>('/complaints', {
          params: { ...filterParams, ...extra, page: currentPage },
        });
        if (cancelled) return;
        setComplaints(response.data);
        setTotal(Number(response.headers['x-total-complaints'] || 0));
      } catch {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao carregar as denúncias.',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filterParams, currentPage, viewMode, user.id, addToast]);

  useEffect(() => {
    const load = async () => {
      try {
        const companiesRes = await api.get<ICompany[]>('/companies');
        setCompanies(companiesRes.data);

        if (user.role === 'admin') {
          const [unionsRes, statesRes] = await Promise.all([
            api.get<ISyndicate[]>('/unions'),
            api.get<IState[]>('/ibge/states'),
          ]);
          setUnions(unionsRes.data);
          setStates(statesRes.data);
        }
      } catch {
        /* silent */
      }
    };
    load();
  }, [user.role]);

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'protocol',
        label: 'Protocolo',
        type: 'text',
        placeholder: 'Digite o protocolo',
        span: 3,
      },
      {
        key: 'company_id',
        label: 'Empresa',
        type: 'select',
        placeholder: 'Todas',
        options: companies.map(c => ({ value: c.id, label: c.nome_fantasia })),
        span: 3,
      },
      {
        key: 'priority',
        label: 'Prioridade',
        type: 'select',
        placeholder: 'Todas',
        options: PRIORITY_OPTIONS,
        span: 2,
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Todos',
        options: STATUS_OPTIONS,
        span: 2,
      },
    ];

    if (user.role === 'admin') {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 3,
      });
      base.push({
        key: 'state',
        label: 'Estado',
        type: 'select',
        placeholder: 'Todos',
        options: states.map(s => ({ value: s.uf, label: s.uf })),
        span: 2,
      });
    }

    return base;
  }, [companies, unions, states, user.role]);

  const columns = useMemo<Column<IComplaint>[]>(
    () => [
      {
        key: 'protocol',
        label: 'Protocolo',
        width: '13%',
        render: c => (
          <span style={{ fontWeight: 700, color: '#0f172a' }}>
            {c.protocol}
          </span>
        ),
      },
      {
        key: 'company',
        label: 'Empresa',
        render: c => c.company?.nome_fantasia || '—',
      },
      {
        key: 'city',
        label: 'Cidade',
        width: '15%',
        render: c => c.city?.name || 'Não especificado',
      },
      {
        key: 'priority',
        label: 'Prioridade',
        width: '11%',
        render: c => {
          const label = PRIORITY_OPTIONS.find(
            p => p.value === String(c.priority),
          )?.label;
          return (
            <PriorityBadge $tone={PRIORITY_TONE[c.priority] || 'neutral'}>
              {label || '—'}
            </PriorityBadge>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: '11%',
        render: c => (
          <StatusBadge $tone={STATUS_TONE[c.status] || 'neutral'}>
            {STATUS_LABEL[c.status] || '—'}
          </StatusBadge>
        ),
      },
      {
        key: 'created_at',
        label: 'Recebido em',
        width: '13%',
        render: c => format(new Date(c.created_at), "dd/MM/yyyy 'às' HH:mm"),
      },
      {
        key: 'updated_at',
        label: 'Última atualização',
        width: '13%',
        render: c => format(new Date(c.updated_at), "dd/MM/yyyy 'às' HH:mm"),
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  const switchView = (mode: ViewMode) => {
    setViewMode(mode);
    setCurrentPage(1);
  };

  return (
    <BasePage title="Denúncias">
      <Container fluid style={{ padding: 0 }}>
        <DataTable<IComplaint>
          title="Denúncias"
          subtitle="Acompanhe, filtre e atue sobre os protocolos recebidos"
          columns={columns}
          data={complaints}
          rowKey="id"
          loading={loading}
          page={currentPage}
          total={total}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <>
              <QuickButton
                type="button"
                $active={viewMode === 'all'}
                onClick={() => switchView('all')}
              >
                <FiInbox size={14} />
                Todas
              </QuickButton>
              <QuickButton
                type="button"
                $active={viewMode === 'mine'}
                onClick={() => switchView('mine')}
              >
                <FiUser size={14} />
                Meus protocolos
              </QuickButton>
              <QuickButton
                type="button"
                $active={viewMode === 'archived'}
                onClick={() => switchView('archived')}
              >
                <FiArchive size={14} />
                Arquivadas
              </QuickButton>
            </>
          )}
          rowActions={row => (
            <ActionButton
              type="button"
              onClick={() => history.push(`/complaints/${row.id}`)}
            >
              <FiEye size={14} />
              Visualizar
            </ActionButton>
          )}
          emptyMessage="Nenhuma denúncia encontrada."
        />
      </Container>
    </BasePage>
  );
};

export default Complaints;
